/**
 * A chord chart as data: each chord with the beats it is held for, on one timeline.
 *
 *   { version: 2, events: [{ chord: 'C', beats: 3 }, { rest: true, beats: 1 }, …],
 *     lineBreaks: [4], sectionBreaks: [], melodyBars: [2] }
 *
 * Bars are not stored — they come from the song's time signature — so a chord held across a bar
 * line is one event and there is no tie to write. Layout sits apart from timing, as the bars a new
 * line or a new section (instrument) starts on. `melodyBars` are the bars played as single notes.
 * `countInEntry` says the first bar sounds over the count-in, coming in where the rest it opens on ends.
 *
 * The builder, the chart and the player all work on the bar-line grid (chordBars.js), so this is the
 * conversion both ways between that grid and what a part stores.
 */

import { sliceCell } from './barSlices';
import {
  BEAT_EPSILON,
  cellChordBeats,
  countGridBars,
  formatBarLines,
  isBarLine,
  isMelodyCell,
  normalizeGrid,
  sectionsFromBreaks,
  splitBarBlocks,
} from './chordBars';
import { isInstrumentalPart } from './parts';

export const CHART_DATA_VERSION = 2;

const barLength = (beatsPerBar) => (Number(beatsPerBar) > 0 ? Number(beatsPerBar) : 4);

const sum = (values) => values.reduce((total, value) => total + value, 0);

// Events worth reading: a chart with no chord struck in it is no chart
const readEvents = (chart) => (chart?.events || []).filter((event) => Number(event?.beats) > 0);

export const hasChartData = (chart) => readEvents(chart).some((event) => event.chord);

/** Whether the chart has beats nobody has written yet — the builder won't save one that does. */
export const hasMissingBeats = (chart) => readEvents(chart).some((event) => event.missing);

/**
 * A grid, as the builder holds it, turned into a chart.
 *
 * Each cell is read by cellChordBeats, the one reading of a cell, and a part-written bar gives what
 * is left of it to its last chord, as formatCell does on the way out. An empty cell or a tie adds to
 * whatever is sounding — a rest included; with nothing sounding yet it is a rest.
 *
 * @param {object} options
 * @param {boolean} options.countInEntry - The first bar sounds over the count-in
 * @returns {object|null} The chart, or null when no chord is written anywhere in the grid
 */
export const gridToChartData = (lines, sections, beatsPerBar, { countInEntry = false } = {}) => {
  const bar = barLength(beatsPerBar);
  const grid = normalizeGrid(lines, sections);
  const events = [];
  const lineBreaks = [];
  const sectionBreaks = [];
  const melodyBars = [];
  let bars = 0;

  const hold = (beats) => {
    const last = events[events.length - 1];

    if (last) last.beats += beats;
    else events.push({ rest: true, beats });
  };

  // Two rests in a row are one silence
  const rest = (beats) => {
    if (events[events.length - 1]?.rest) hold(beats);
    else events.push({ rest: true, beats });
  };

  grid.lines.forEach((cells, line) => {
    if (line > 0) {
      lineBreaks.push(bars);
      if (grid.sections[line] > grid.sections[line - 1]) sectionBreaks.push(bars);
    }

    cells.forEach((cell) => {
      const entries = cellChordBeats(cell, bar);

      if (isMelodyCell(cell)) melodyBars.push(bars);

      if (entries.length === 0) hold(bar);
      else {
        // A lone chord written bare holds the whole bar
        const lengths = entries.map(({ beats }) => beats ?? bar);
        const short = bar - sum(lengths);

        if (short > BEAT_EPSILON) lengths[lengths.length - 1] += short;

        entries.forEach(({ chord, missing, rest: isRest }, index) => {
          if (missing) events.push({ missing: true, beats: lengths[index] });
          else if (isRest) rest(lengths[index]);
          else if (chord === null) hold(lengths[index]);
          else events.push({ chord, beats: lengths[index] });
        });
      }

      bars += 1;
    });
  });

  if (!events.some((event) => event.chord)) return null;

  return {
    version: CHART_DATA_VERSION,
    events,
    lineBreaks,
    sectionBreaks,
    ...(melodyBars.length > 0 ? { melodyBars } : null),
    ...(countInEntry ? { countInEntry: true } : null),
  };
};

/**
 * A chart as the grid the builder, the chart and the player draw from: `{ lines, sections }`.
 *
 * The timeline is cut into bars of the song's time signature, and a chord that runs on past a bar
 * line carries into the next bar as a tie. A rest is written again in each bar it reaches, since
 * a tie would read as the chord before it still ringing. A chart that no longer ends on a bar line — its song
 * changed time signature — has its last chord hold to the end of the bar, or with `openEnd` shows
 * the rest of that bar as missing beats, which is what the builder wants so it can be filled.
 *
 * @returns {{ lines: string[][], sections: number[], countInEntry?: boolean } | null}
 */
export const chartDataToGrid = (chart, beatsPerBar, { openEnd = false } = {}) => {
  if (!hasChartData(chart)) return null;

  const bar = barLength(beatsPerBar);
  const cells = [];
  let at = 0;

  readEvents(chart).forEach((event) => {
    let left = Number(event.beats);
    let first = true;

    while (left > BEAT_EPSILON) {
      const index = Math.floor((at + BEAT_EPSILON) / bar);
      const room = (index + 1) * bar - at;
      const take = left <= room + BEAT_EPSILON ? left : room;

      if (!cells[index]) cells[index] = [];

      cells[index].push({
        chord: first && event.chord ? event.chord : null,
        beats: take,
        ...(event.missing ? { missing: true } : null),
        ...(event.rest ? { rest: true } : null),
      });

      at += take;
      left -= take;
      first = false;
    }
  });

  const short = cells.length * bar - at;

  if (short > BEAT_EPSILON) {
    const last = cells[cells.length - 1];

    if (openEnd) last.push({ chord: null, beats: short, missing: true });
    else last[last.length - 1].beats += short;
  }

  const count = cells.length;
  const sectionStarts = new Set(chart.sectionBreaks || []);
  const starts = [
    0,
    ...[...new Set([...(chart.lineBreaks || []), ...(chart.sectionBreaks || [])])]
      .filter((start) => Number.isInteger(start) && start > 0 && start < count)
      .sort((a, b) => a - b),
  ];

  const melody = new Set(chart.melodyBars || []);
  const lines = starts.map((start, i) =>
    cells.slice(start, starts[i + 1] ?? count).map((cell, j) => sliceCell(cell, bar, { melody: melody.has(start + j) })),
  );
  const opening = starts.flatMap((start, line) => (line > 0 && sectionStarts.has(start) ? [line] : []));

  return {
    lines,
    sections: sectionsFromBreaks(opening, lines.length),
    ...(chart.countInEntry ? { countInEntry: true } : null),
  };
};

/**
 * Every chart written in a text, as one grid.
 *
 * A single blank row inside a chart is already a section break. Charts further apart than that —
 * two in one part, or words between them — are joined as sections too, one after another.
 *
 * @returns {{ lines: string[][], sections: number[] } | null}
 */
export const readBarBlocks = (text) => {
  const blocks = splitBarBlocks(text).filter((segment) => segment.type === 'bars');

  if (blocks.length === 0) return null;

  const lines = [];
  const sections = [];

  blocks.forEach((block) => {
    const offset = sections.length > 0 ? sections[sections.length - 1] + 1 : 0;

    lines.push(...block.lines);
    sections.push(...block.sections.map((section) => section + offset));
  });

  return { lines, sections };
};

/** Every chart written in a text, as one chart — see readBarBlocks. */
export const textToChartData = (text, beatsPerBar) => {
  const grid = readBarBlocks(text);

  return grid ? gridToChartData(grid.lines, grid.sections, beatsPerBar) : null;
};

/** The chart as bar-line text, the way it was stored before chartData. */
export const chartDataToText = (chart, beatsPerBar) => {
  const grid = chartDataToGrid(chart, beatsPerBar);

  return grid ? formatBarLines(grid.lines, beatsPerBar, grid.sections) : '';
};

/** A text with its charts taken out and only the words left. */
export const removeBarLines = (text) =>
  splitBarBlocks(text)
    .filter((segment) => segment.type === 'text')
    .map((segment) => segment.text)
    .join('\n');

/** Bars in the chart at the song's time signature. */
export const countChartBars = (chart, beatsPerBar) => {
  const grid = chartDataToGrid(chart, beatsPerBar);

  return grid ? countGridBars(grid.lines, grid.sections) : 0;
};

/** Every chord the chart strikes, in order. */
export const getChartDataChords = (chart) => (chart?.events || []).flatMap((event) => (event.chord ? [event.chord] : []));

/**
 * The chart with its chords put through a function that rewrites chords in text — transposing, say.
 *
 * The chords go through as a bar line, the form every text transposer already reads, so a chord
 * moves here exactly as it did in the bar-line text a chart used to be stored as.
 */
export const mapChartDataChords = (chart, rewriteText) => {
  const chords = getChartDataChords(chart);

  if (chords.length === 0) return chart;

  const rewritten = String(rewriteText(`| ${chords.join(' | ')} |`) ?? '')
    .split('|')
    .map((cell) => cell.trim())
    .filter(Boolean);

  if (rewritten.length !== chords.length) return chart;

  let next = 0;

  return {
    ...chart,
    events: chart.events.map((event) => (event.chord ? { ...event, chord: rewritten[next++] } : event)),
  };
};

/**
 * Whether bar lines are typed into an instrumental's lyrics. That is still a way to write a chart:
 * saving the part moves them into its chart (importLyricsChart).
 */
export const hasLyricsChart = (part) =>
  isInstrumentalPart(part?.name) && (part?.lyrics ?? '').split(/\r\n|\r|\n/).some(isBarLine);

/** The part with the bar lines typed into its lyrics moved into its chart, replacing any it had. */
export const importLyricsChart = (part, beatsPerBar) => {
  if (!hasLyricsChart(part)) return part;

  const chartData = textToChartData(part.lyrics, beatsPerBar);

  return { ...part, lyrics: removeBarLines(part.lyrics).trim(), ...(chartData && { chartData }) };
};
